import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import DashboardLayout from '../components/DashboardLayout';
import { Music2, Calendar, Clock, MoreVertical, Loader2, PlayCircle, Share2, Download, Upload } from 'lucide-react';

interface Release {
  id: string;
  title: string;
  artist_name: string;
  release_date: string;
  status: string;
  created_at: string;
  cover_url?: string;
}

const Dashboard = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReleases();
  }, []);

  const fetchReleases = async () => {
    try {
      const { data, error } = await supabase
        .from('releases')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReleases(data || []);
    } catch (error) {
      console.error('Error fetching releases:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Your Releases</h1>
            <p className="text-gray-400">Manage and track your music distribution</p>
          </div>
          <a
            href="/dashboard/upload"
            className="bg-gradient-to-r from-purple-500 to-purple-700 px-6 py-3 rounded-xl text-white font-medium hover:from-purple-600 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/25 inline-flex items-center gap-2 group"
          >
            <Music2 className="h-5 w-5 group-hover:rotate-12 transition-transform" />
            New Release
          </a>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : releases.length === 0 ? (
          <div className="text-center py-16 bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-purple-500/20">
            <div className="bg-purple-500/20 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Music2 className="h-8 w-8 text-purple-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">No releases yet</h3>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Start your music distribution journey by uploading your first release
            </p>
            <a
              href="/dashboard/upload"
              className="bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-xl text-white font-medium hover:from-purple-600 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/25 inline-flex items-center gap-3 group"
            >
              <Upload className="h-5 w-5 group-hover:translate-y-[-2px] transition-transform" />
              Upload Music
            </a>
          </div>
        ) : (
          <div className="grid gap-6">
            {releases.map((release) => (
              <div
                key={release.id}
                className="bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-purple-500/20 p-6 hover:border-purple-500/40 transition-all group"
              >
                <div className="flex items-center gap-6">
                  <div className="relative shrink-0">
                    {release.cover_url ? (
                      <img
                        src={release.cover_url}
                        alt={release.title}
                        className="w-24 h-24 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-purple-500/20 rounded-xl flex items-center justify-center">
                        <Music2 className="h-8 w-8 text-purple-400" />
                      </div>
                    )}
                    <button className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                      <PlayCircle className="h-8 w-8 text-white" />
                    </button>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">{release.title}</h3>
                        <p className="text-gray-400">{release.artist_name}</p>
                      </div>
                      <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                        release.status === 'published'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {release.status.charAt(0).toUpperCase() + release.status.slice(1)}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">
                          Release: {new Date(release.release_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm">
                          Created: {new Date(release.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        <button className="p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded-lg transition-colors">
                          <Share2 className="h-5 w-5" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded-lg transition-colors">
                          <Download className="h-5 w-5" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded-lg transition-colors">
                          <MoreVertical className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;